import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Send,
    AlertCircle,
    MessageCircle,
    Clock,
    User,
    Loader2,
    ShieldCheck,
    Download,
    X,
    ChevronLeft,
    ChevronRight,
    Pencil,
    Check,
    Image as ImageIcon,
    Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { useShareAIEdit, ShareEditBanner } from "@/hooks/useShareAIEdit";
import { lockBodyScroll } from "@/lib/lockBodyScroll";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const NAME_KEY = "preview_share_viewer_name";

function mmss(seconds) {
    const s = Math.max(0, Math.floor(seconds || 0));
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function fmtDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleString("nl-BE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

export default function PublicPreview() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const videoRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [text, setText] = useState("");

    // Viewer identity (cached so they don't have to retype)
    const [viewerName, setViewerName] = useState("");
    const [needsName, setNeedsName] = useState(false);
    const [tempName, setTempName] = useState("");

    // Lightbox state for photo galleries
    const [lightboxIdx, setLightboxIdx] = useState(null); // null = closed
    // Embedded in ProjectDetail with AI-edit rights → show per-photo edit buttons
    const editEnabled = new URLSearchParams(window.location.search).get("edit") === "1";
    const embedded = new URLSearchParams(window.location.search).get("embedded") === "1";
    const requestEdit = (i) => {
        try { window.parent.postMessage({ type: "sf-edit-photo", index: i }, "*"); } catch (_) { /* noop */ }
    };

    // Standalone share link: AI-bewerken via gedeelde hook (login/registreer
    // popup + claim-access + editor). Embedded (in ProjectDetail) blijft via
    // postMessage naar de parent lopen.
    const {
        handleEditClick: shareEditClick,
        creditsBadge: editCreditsBadge,
        modals: editModals,
    } = useShareAIEdit({
        token,
        photos: data?.photos || [],
        refetch: () => load(),
        enabled: !embedded && !!data?.is_photos,
        onBeforeOpen: () => setLightboxIdx(null),
    });
    const showEdit = editEnabled || (!embedded);
    const handleEditClick = (i) => {
        if (editEnabled) { requestEdit(i); return; }
        shareEditClick(i);
    };
    const touchStartX = useRef(null);
    const lightboxImgRef = useRef(null);

    // Photo annotation / per-photo comment state
    const [drawMode, setDrawMode] = useState(false);
    const [annotationDataUrl, setAnnotationDataUrl] = useState(null);
    const [annotationResetKey, setAnnotationResetKey] = useState(0);
    const [photoCommentText, setPhotoCommentText] = useState("");
    const [photoCommentSubmitting, setPhotoCommentSubmitting] = useState(false);
    const [activeAnnotationUrl, setActiveAnnotationUrl] = useState(null); // for "view annotation" overlay
    const [compareMode, setCompareMode] = useState(false); // shows before/after slider for AI-edited photos

    // Bulk-download state for photo galleries — fires individual <a download> clicks
    // one at a time with spacing so Chrome doesn't block as "multiple downloads"
    const [downloading, setDownloading] = useState({ active: false, done: 0, total: 0, paused: false });
    const downloadAllPhotos = async () => {
        const aiUrls = data?.photos || [];
        const origUrls = data?.photos_original || [];
        const prev = data?.photo_previous_urls || {}; // { "0": "/api/uploads/...", ... }
        // Build a unified queue:
        //   1. AI/displayed photos (data.photos) — every tile the viewer sees
        //   2. Bulk "originals" array (data.photos_original) — pre-AI raw uploads when present
        //   3. Per-index "previous" URLs (data.photo_previous_urls) — the actual
        //      original of any photo that has been replaced by an AI version.
        //      This is what makes the "give me originals AND AI versions" promise
        //      work on photo sets where the broker only ever uploaded one set
        //      and the admin replaced some of them with AI edits.
        const hasExtraOriginals = origUrls.length > 0 || Object.keys(prev).length > 0;
        const queue = [];
        aiUrls.forEach((url, i) => queue.push({
            url, idx: i + 1,
            label: hasExtraOriginals ? "AI" : "",
        }));
        origUrls.forEach((url, i) => queue.push({
            url, idx: i + 1,
            label: hasExtraOriginals ? "Origineel" : "",
        }));
        // The per-index previous URLs only fire when the bulk originals array
        // is empty — otherwise we'd ship duplicates.
        if (origUrls.length === 0) {
            Object.entries(prev).forEach(([k, url]) => {
                if (!url) return;
                queue.push({ url, idx: parseInt(k, 10) + 1, label: "Origineel" });
            });
        }
        if (queue.length === 0) return;
        const address = (data?.title || data?.subtitle || "fotoreeks").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
        setDownloading({ active: true, done: 0, total: queue.length, paused: false });
        toast.success(
            queue.length > 50
                ? `Download van ${queue.length} foto's gestart — Chrome zal vragen om meerdere downloads toe te staan. Klik Toestaan.`
                : "Download gestart — sta meerdere downloads toe als Chrome het vraagt",
            { duration: 6000 },
        );

        for (let i = 0; i < queue.length; i++) {
            const { url, idx, label } = queue[i];
            const original = url.split("/").pop().split("?")[0];
            const ext = original.includes(".") ? original.slice(original.lastIndexOf(".")) : ".jpg";
            const prefix = label ? `${label} - ` : "";
            const niceName = `${prefix}${address} - foto ${String(idx).padStart(2, "0")}${ext}`;
            const a = document.createElement("a");
            a.href = `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(original)}?name=${encodeURIComponent(niceName)}`;
            a.download = niceName;
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setDownloading((s) => ({ ...s, done: i + 1 }));
            const delay = i === 0 ? 800 : (queue.length > 100 ? 600 : 400);
            await new Promise((r) => setTimeout(r, delay));
        }
        toast.success(`Alle ${queue.length} foto's gedownload — kijk in je Downloads-map`);
        setTimeout(() => setDownloading({ active: false, done: 0, total: 0, paused: false }), 3000);
    };

    useEffect(() => {
        const cached = (typeof window !== "undefined" && localStorage.getItem(NAME_KEY)) || "";
        if (cached) setViewerName(cached);
    }, []);

    const load = async () => {
        try {
            const { data: res } = await api.get(`/preview/${token}`);
            setData(res);
        } catch (e) {
            const status = e.response?.status;
            if (status === 410) {
                setError("Deze deellink is verlopen. Vraag aan de admin om hem te verlengen.");
            } else {
                setError(e.response?.data?.detail || "Deze link is niet meer geldig");
            }
        }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    // Lightbox: keyboard navigation (ESC / ← / →)
    useEffect(() => {
        if (lightboxIdx === null) return;
        const photos = data?.photos || [];
        const handler = (e) => {
            // Ignore arrow keys while typing in the comment textarea / input
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea") {
                if (e.key === "Escape") setLightboxIdx(null);
                return;
            }
            if (e.key === "Escape") setLightboxIdx(null);
            else if (e.key === "ArrowLeft") prevPhoto();
            else if (e.key === "ArrowRight") nextPhoto();
        };
        window.addEventListener("keydown", handler);
        // Lock body scroll while lightbox is open
        const release = lockBodyScroll(true);
        return () => {
            window.removeEventListener("keydown", handler);
            release();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightboxIdx, data]);

    const openLightbox = (i) => {
        setLightboxIdx(i);
        setDrawMode(false);
        setAnnotationDataUrl(null);
        setPhotoCommentText("");
        setAnnotationResetKey((k) => k + 1);
        setCompareMode(false);
    };
    const closeLightbox = () => {
        setLightboxIdx(null);
        setDrawMode(false);
        setAnnotationDataUrl(null);
        setPhotoCommentText("");
        setActiveAnnotationUrl(null);
        setCompareMode(false);
    };
    const nextPhoto = () => {
        const photos = data?.photos || [];
        if (!photos.length) return;
        setLightboxIdx((i) => (i === null ? null : (i + 1) % photos.length));
        setDrawMode(false);
        setAnnotationDataUrl(null);
        setPhotoCommentText("");
        setActiveAnnotationUrl(null);
        setAnnotationResetKey((k) => k + 1);
        setCompareMode(false);
    };
    const prevPhoto = () => {
        const photos = data?.photos || [];
        if (!photos.length) return;
        setLightboxIdx((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
        setDrawMode(false);
        setAnnotationDataUrl(null);
        setPhotoCommentText("");
        setActiveAnnotationUrl(null);
        setAnnotationResetKey((k) => k + 1);
        setCompareMode(false);
    };
    const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 50) (dx < 0 ? nextPhoto() : prevPhoto)();
        touchStartX.current = null;
    };

    const seekTo = (sec) => {
        const v = videoRef.current;
        if (!v) return;
        v.currentTime = sec;
        v.play().catch(() => {});
    };

    const submitComment = async () => {
        if (!text.trim()) {
            toast.error("Schrijf een bericht");
            return;
        }
        if (!viewerName) {
            setTempName("");
            setNeedsName(true);
            return;
        }
        setSubmitting(true);
        try {
            const ts = videoRef.current?.currentTime ?? currentTime;
            const { data: comment } = await api.post(`/preview/${token}/comments`, {
                timestamp_seconds: ts,
                text: text.trim(),
                author_name: viewerName,
            });
            setData((prev) => ({ ...prev, comments: [...(prev?.comments || []), comment] }));
            setText("");
            toast.success("Opmerking geplaatst");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Plaatsen mislukt");
        }
        setSubmitting(false);
    };

    // Submit a per-photo comment (optionally with a drawn annotation overlay).
    const submitPhotoComment = async () => {
        if (lightboxIdx === null) return;
        if (!photoCommentText.trim()) {
            toast.error("Schrijf eerst een bericht");
            return;
        }
        if (!viewerName) {
            setTempName("");
            setNeedsName(true);
            return;
        }
        setPhotoCommentSubmitting(true);
        try {
            const img = lightboxImgRef.current;
            const payload = {
                timestamp_seconds: 0,
                text: photoCommentText.trim(),
                author_name: viewerName,
                photo_index: lightboxIdx,
                photo_url: data.photos[lightboxIdx],
                annotation_data_b64: annotationDataUrl || null,
                photo_width: img?.naturalWidth || null,
                photo_height: img?.naturalHeight || null,
            };
            const { data: comment } = await api.post(`/preview/${token}/comments`, payload);
            setData((prev) => ({ ...prev, comments: [...(prev?.comments || []), comment] }));
            setPhotoCommentText("");
            setAnnotationDataUrl(null);
            setDrawMode(false);
            setAnnotationResetKey((k) => k + 1);
            toast.success("Opmerking verzonden — de admin krijgt een melding");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Plaatsen mislukt");
        }
        setPhotoCommentSubmitting(false);
    };

    // Comments grouped by photo_index for the current gallery
    const commentsByPhoto = useMemo(() => {
        const map = {};
        for (const c of data?.comments || []) {
            if (c.photo_index === null || c.photo_index === undefined) continue;
            (map[c.photo_index] = map[c.photo_index] || []).push(c);
        }
        return map;
    }, [data?.comments]);

    const saveName = () => {
        const n = tempName.trim();
        if (!n) {
            toast.error("Geef een naam op");
            return;
        }
        localStorage.setItem(NAME_KEY, n);
        setViewerName(n);
        setNeedsName(false);
    };

    if (loading) {
        return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-zinc-500">Laden…</div>;
    }
    if (error || !data) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
                <div className="text-center max-w-md">
                    <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                    <h1 className="font-display font-bold text-2xl mb-2">Niet beschikbaar</h1>
                    <p className="text-sm text-zinc-500">{error || "Deze link is niet meer geldig."}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950" data-testid="public-preview-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-2 mb-3">
                        <Send className="h-4 w-4 text-[#E5A93D]" />
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#E5A93D]">
                            Vastgoed Social Factory · {data.asset_label || "Preview"}
                        </span>
                    </div>
                    <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight" data-testid="preview-title">
                        {data.address_street}
                    </h1>
                    <div className="text-zinc-500 text-sm mt-1">{data.address_city}</div>
                </div>

                {/* Video — hidden for photo galleries */}
                {!data.is_photos && (
                    <>
                        <div className="bg-zinc-50 rounded-md overflow-hidden border border-zinc-100 mb-4">
                            <video
                                ref={videoRef}
                                src={data.stream_url || data.video_url || data.preview_video_url}
                                controls
                                playsInline
                                className="w-full max-h-[70vh] object-contain bg-zinc-50"
                                onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                                data-testid="preview-video"
                            />
                        </div>

                        {data.is_final && (data.video_url || data.preview_video_url) && (
                            <div className="mb-6 flex justify-end">
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/preview/${token}/download`}
                                    className="inline-flex items-center gap-1.5 text-xs px-4 h-10 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                                    data-testid="public-download-btn"
                                >
                                    <Download className="h-3.5 w-3.5" /> Download {data.asset_label?.toLowerCase() || "video"}
                                </a>
                            </div>
                        )}
                    </>
                )}

                {/* Photo gallery */}
                {data.is_photos && (
                    <div className="mb-8" data-testid="public-photo-gallery">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <div className="text-sm text-zinc-500">
                                <span className="text-zinc-950 font-semibold">{data.photos?.length || 0}</span> {data.photos?.length === 1 ? "foto" : "foto's"} in deze reeks
                                {(() => {
                                    const aiCount = Object.values(data.photo_previous_urls || {}).filter(Boolean).length
                                        + (data.photos_original?.length || 0);
                                    if (aiCount > 0) {
                                        return (
                                            <span className="text-zinc-500"> · <span className="text-[#E5A93D] font-semibold">{aiCount}</span> AI-bewerkt</span>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                            {!embedded && editCreditsBadge}
                            {data.photos && data.photos.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => downloadAllPhotos()}
                                    disabled={downloading.active}
                                    className="inline-flex items-center gap-1.5 text-xs px-4 h-10 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="public-download-all-btn"
                                >
                                    <Download className="h-3.5 w-3.5" />
                                    {downloading.active ? `Bezig… ${downloading.done}/${downloading.total}` : "Download"}
                                </button>
                            )}
                        </div>
                        {showEdit && data.photos && data.photos.length > 0 && (
                            <ShareEditBanner />
                        )}
                        {(!data.photos || data.photos.length === 0) ? (
                            <div className="text-zinc-500 text-sm py-10 text-center bg-[#FFFFFF] border border-zinc-100 rounded-md">
                                Geen foto's beschikbaar.
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {data.photos.map((url, i) => {
                                    const count = (commentsByPhoto[i] || []).length;
                                    const hasAi = !!(data.photo_previous_urls && data.photo_previous_urls[String(i)]);
                                    // Use a small thumb (400px) for grid tiles — generated + cached
                                    // server-side. Falls back to original URL for non-/api/uploads/ paths.
                                    const thumbMatch = /\/api\/uploads\/([^/?#]+)$/.exec((url || "").split("?")[0]);
                                    const thumbSrc = thumbMatch ? `/api/thumb/${thumbMatch[1]}?w=400` : url;
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => openLightbox(i)}
                                            className="relative block bg-[#FFFFFF] border border-zinc-100 rounded-md overflow-hidden hover:border-[#E5A93D]/50 transition group focus:outline-none focus:ring-2 focus:ring-[#E5A93D]/60"
                                            data-testid={`public-photo-${i}`}
                                            aria-label={`Open foto ${i + 1}`}
                                        >
                                            <img
                                                src={thumbSrc}
                                                alt={`Foto ${i + 1}`}
                                                loading="lazy"
                                                className="w-full aspect-square object-cover group-hover:scale-[1.03] transition"
                                            />
                                            {hasAi && (
                                                <span
                                                    className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-black/75 text-[#E5A93D] border border-[#E5A93D]/40 rounded-sm px-1.5 h-5"
                                                    data-testid={`photo-ai-badge-${i}`}
                                                    title="Bewerkt met AI — klik om te vergelijken met origineel"
                                                >
                                                    <Sparkles className="h-3 w-3" />
                                                    AI
                                                </span>
                                            )}
                                            {count > 0 && (
                                                <span
                                                    className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E5A93D] text-black rounded-sm px-1.5 h-5"
                                                    data-testid={`photo-comment-badge-${i}`}
                                                    title={`${count} opmerking${count === 1 ? "" : "en"}`}
                                                >
                                                    <MessageCircle className="h-3 w-3" />
                                                    {count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Identity bar — hidden for photo galleries */}
                {!data.is_photos && (
                    <div className="flex items-center gap-3 p-3 bg-[#FFFFFF] border border-zinc-100 rounded-md mb-6">
                    <User className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                    {viewerName ? (
                        <div className="flex items-center justify-between flex-1 gap-3">
                            <div className="text-sm">
                                <span className="text-zinc-500">Reageer als </span>
                                <span className="text-zinc-950 font-medium">{viewerName}</span>
                            </div>
                            <button
                                onClick={() => { setNeedsName(true); setTempName(viewerName); }}
                                className="text-xs text-zinc-500 hover:text-zinc-950"
                                data-testid="change-name-btn"
                            >
                                Wijzig
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => { setNeedsName(true); setTempName(""); }}
                            className="text-sm text-zinc-600 hover:text-zinc-950 text-left flex-1"
                            data-testid="set-name-btn"
                        >
                            Klik hier om je naam in te vullen
                        </button>
                    )}
                    </div>
                )}

                {/* New comment form — hidden for photo galleries */}
                {!data.is_photos && (
                <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-5 mb-8" data-testid="new-comment-block">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <MessageCircle className="h-4 w-4 text-[#E5A93D]" />
                        <h3 className="font-display font-bold text-sm tracking-tight">Plaats een opmerking</h3>
                        <span className="ml-auto inline-flex items-center gap-1 text-xs text-[#E5A93D] bg-[#E5A93D]/10 border border-[#E5A93D]/30 rounded-sm px-2 py-1">
                            <Clock className="h-3 w-3" /> Bij {mmss(currentTime)}
                        </span>
                    </div>
                    <Textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={3}
                        placeholder="Wat valt je op op deze plek in de video? Tip: pauzeer of spoel naar het juiste moment voordat je verstuurt."
                        className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                        data-testid="new-comment-text"
                    />
                    <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                        <div className="text-[11px] text-zinc-500">Je opmerking is direct zichtbaar voor de admin.</div>
                        <Button
                            onClick={submitComment}
                            disabled={submitting || !text.trim()}
                            className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-10 px-5 active:scale-[0.98]"
                            data-testid="submit-comment-btn"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Versturen"}
                        </Button>
                    </div>
                </div>
                )}

                {/* Comments — hidden for photo galleries */}
                {!data.is_photos && (
                <div data-testid="comments-list">
                    <div className="flex items-center gap-2 mb-4">
                        <h3 className="font-display font-bold text-lg tracking-tight">Opmerkingen</h3>
                        <span className="text-zinc-500 text-sm">({data.comments?.length || 0})</span>
                    </div>
                    {(!data.comments || data.comments.length === 0) ? (
                        <div className="text-zinc-500 text-sm py-6 text-center bg-[#FFFFFF] border border-zinc-100 rounded-md">
                            Nog geen opmerkingen — wees de eerste.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.comments.map((c) => (
                                <CommentItem key={c.id} c={c} onJump={() => seekTo(c.timestamp_seconds || 0)} />
                            ))}
                        </div>
                    )}
                </div>
                )}

                {/* Subtle branding footer — visible on all public share pages */}
                <footer className="mt-12 pt-6 border-t border-zinc-100 flex items-center justify-center" data-testid="public-share-branding">
                    <a
                        href="https://vastgoed.social-factory.be"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 text-zinc-500 hover:text-zinc-600 transition group"
                        aria-label="Vastgoed Social Factory"
                    >
                        <img
                            src="/social-factory-logo.png"
                            alt="Social Factory"
                            className="h-5 w-auto opacity-50 group-hover:opacity-90 transition invert"
                        />
                        <span className="text-[10px] uppercase tracking-[0.25em]">
                            Powered by <span className="text-zinc-500 group-hover:text-[#E5A93D] transition">Vastgoed Social Factory</span>
                        </span>
                    </a>
                </footer>
            </div>

            {/* Login/registreer-popup + AI-editor (gedeelde hook) */}
            {!embedded && editModals}

            {/* Lightbox for photo galleries */}
            {lightboxIdx !== null && data?.photos?.length > 0 && (
                <div
                    className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-start select-none overflow-y-auto"
                    onClick={drawMode ? undefined : closeLightbox}
                    onTouchStart={drawMode ? undefined : onTouchStart}
                    onTouchEnd={drawMode ? undefined : onTouchEnd}
                    data-testid="public-lightbox"
                >
                    {/* Top bar — mobiel: compacte iconen/korte labels op één rij */}
                    <div className="sticky top-0 inset-x-0 flex items-center justify-between gap-2 p-3 lg:p-6 z-30 pointer-events-none w-full">
                        <div className="text-xs font-mono text-zinc-400 bg-black/50 backdrop-blur-sm px-2.5 sm:px-3 h-9 inline-flex items-center rounded-sm border border-white/10 pointer-events-auto whitespace-nowrap shrink-0" data-testid="lightbox-counter">
                            {lightboxIdx + 1} / {data.photos.length}
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2 pointer-events-auto min-w-0">
                            {showEdit && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleEditClick(lightboxIdx); }}
                                    className="inline-flex items-center gap-1.5 text-xs px-2.5 sm:px-3 h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95 whitespace-nowrap shrink-0"
                                    data-testid="lightbox-edit-btn"
                                    title="Bewerk deze foto met AI — credits worden afgeboekt"
                                >
                                    <Sparkles className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">Bewerk met AI</span>
                                    <span className="sm:hidden">Bewerk</span>
                                </button>
                            )}
                            {data.photo_previous_urls && data.photo_previous_urls[String(lightboxIdx)] && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setCompareMode((v) => !v); }}
                                    className={`inline-flex items-center gap-1.5 text-xs px-2.5 sm:px-3 h-9 rounded-sm transition active:scale-95 backdrop-blur-sm border whitespace-nowrap shrink-0 ${compareMode ? "bg-[#E5A93D] text-black border-[#E5A93D] font-bold" : "bg-zinc-100 hover:bg-zinc-200 text-black border-zinc-200"}`}
                                    data-testid="compare-toggle-btn"
                                    title="Vergelijk met origineel (voor AI-bewerking)"
                                >
                                    <Sparkles className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">{compareMode ? "Toon nieuwe" : "Vergelijk met origineel"}</span>
                                    <span className="sm:hidden">{compareMode ? "Nieuw" : "Origineel"}</span>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    // Download BOTH the AI version (currently shown) AND the
                                    // original (when one exists in photo_previous_urls).
                                    // Same trick as the bulk-download: spawn <a download> clicks
                                    // with a small delay so Chrome doesn't block as "multiple downloads".
                                    const aiUrl = data.photos[lightboxIdx];
                                    const prevUrl = (data.photo_previous_urls || {})[String(lightboxIdx)];
                                    const address = (data?.title || data?.subtitle || "fotoreeks").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
                                    const hasPrev = !!prevUrl;
                                    const queue = [];
                                    if (aiUrl) queue.push({ url: aiUrl, label: hasPrev ? "AI" : "" });
                                    if (prevUrl) queue.push({ url: prevUrl, label: "Origineel" });
                                    if (queue.length === 0) return;
                                    if (hasPrev) {
                                        toast.success("Download van AI + origineel gestart — sta meerdere downloads toe als Chrome het vraagt", { duration: 5000 });
                                    }
                                    for (let i = 0; i < queue.length; i++) {
                                        const { url, label } = queue[i];
                                        const original = url.split("/").pop().split("?")[0];
                                        const ext = original.includes(".") ? original.slice(original.lastIndexOf(".")) : ".jpg";
                                        const prefix = label ? `${label} - ` : "";
                                        const niceName = `${prefix}${address} - foto ${String(lightboxIdx + 1).padStart(2, "0")}${ext}`;
                                        const a = document.createElement("a");
                                        a.href = `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(original)}?name=${encodeURIComponent(niceName)}`;
                                        a.download = niceName;
                                        a.style.display = "none";
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                        if (i < queue.length - 1) await new Promise((r) => setTimeout(r, 500));
                                    }
                                }}
                                className="inline-flex items-center gap-1.5 text-xs px-2.5 sm:px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 backdrop-blur-sm border border-zinc-200 whitespace-nowrap shrink-0"
                                data-testid="lightbox-download-btn"
                                aria-label="Download foto's"
                            >
                                <Download className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">{(data.photo_previous_urls || {})[String(lightboxIdx)] ? "Download (AI + origineel)" : "Download"}</span>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                                className="inline-flex items-center justify-center h-9 w-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 backdrop-blur-sm border border-zinc-200 shrink-0"
                                data-testid="lightbox-close-btn"
                                aria-label="Sluiten"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Image stage */}
                    <div
                        className="relative flex items-center justify-center w-full px-2 lg:px-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Prev arrow */}
                        {data.photos.length > 1 && !drawMode && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                                className="absolute left-2 lg:left-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-full transition active:scale-95 backdrop-blur-sm z-10"
                                data-testid="lightbox-prev-btn"
                                aria-label="Vorige foto"
                            >
                                <ChevronLeft className="h-6 w-6" />
                            </button>
                        )}

                        <div className="relative inline-block">
                            {(() => {
                                const prevByIdx = data.photo_previous_urls || {};
                                const previousUrl = prevByIdx[String(lightboxIdx)];
                                if (compareMode && previousUrl) {
                                    return (
                                        <BeforeAfterSlider
                                            beforeUrl={previousUrl}
                                            afterUrl={data.photos[lightboxIdx]}
                                            className="max-h-[78vh] max-w-[92vw]"
                                            testId="public-before-after-slider"
                                        />
                                    );
                                }
                                return (
                                    <>
                                        <img
                                            ref={lightboxImgRef}
                                            src={data.photos[lightboxIdx]}
                                            alt={`Foto ${lightboxIdx + 1}`}
                                            className="max-h-[78vh] max-w-[92vw] object-contain block"
                                            onClick={(e) => e.stopPropagation()}
                                            onLoad={() => setAnnotationResetKey((k) => k + 1)}
                                            data-testid="lightbox-image"
                                        />
                                        {activeAnnotationUrl && !drawMode && (
                                            <img
                                                src={`${process.env.REACT_APP_BACKEND_URL}${activeAnnotationUrl}`}
                                                alt="Annotatie"
                                                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                                                data-testid="annotation-overlay"
                                            />
                                        )}
                                        <PhotoAnnotator
                                            imageRef={lightboxImgRef}
                                            active={drawMode}
                                            resetSignal={annotationResetKey}
                                            onChange={setAnnotationDataUrl}
                                        />
                                    </>
                                );
                            })()}
                        </div>

                        {/* Next arrow */}
                        {data.photos.length > 1 && !drawMode && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                                className="absolute right-2 lg:right-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-full transition active:scale-95 backdrop-blur-sm z-10"
                                data-testid="lightbox-next-btn"
                                aria-label="Volgende foto"
                            >
                                <ChevronRight className="h-6 w-6" />
                            </button>
                        )}
                    </div>

                    {/* Comment + draw composer */}
                    <div
                        className="w-full max-w-3xl mx-auto px-4 mt-4 mb-8"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="photo-comment-block"
                    >
                        <div className="bg-[#FFFFFF]/95 border border-zinc-200 rounded-md p-4 backdrop-blur-sm">
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                                <MessageCircle className="h-4 w-4 text-[#E5A93D]" />
                                <h3 className="font-display font-bold text-sm tracking-tight">Opmerking op foto {lightboxIdx + 1}</h3>
                                <div className="ml-auto flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = !drawMode;
                                            setDrawMode(next);
                                            if (!next) {
                                                // turning off → clear strokes
                                                setAnnotationDataUrl(null);
                                                setAnnotationResetKey((k) => k + 1);
                                            } else {
                                                setActiveAnnotationUrl(null);
                                            }
                                        }}
                                        className={`inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-sm transition active:scale-95 border ${
                                            drawMode
                                                ? "bg-red-500 hover:bg-red-600 text-zinc-950 border-red-400"
                                                : "bg-zinc-100 hover:bg-zinc-200 text-zinc-950 border-zinc-200"
                                        }`}
                                        data-testid="toggle-draw-btn"
                                    >
                                        {drawMode ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                                        {drawMode ? "Tekenen klaar" : "Teken op foto"}
                                    </button>
                                </div>
                            </div>

                            {drawMode && (
                                <div className="text-[11px] text-zinc-500 mb-3 bg-zinc-100 border border-zinc-200 rounded-sm px-3 py-2">
                                    Teken in <span className="text-red-600 font-medium">rood</span> op de foto om aan te duiden wat aangepast moet worden. Druk daarna op <span className="text-zinc-950 font-medium">"Tekenen klaar"</span> en verzend je opmerking.
                                </div>
                            )}

                            <Textarea
                                value={photoCommentText}
                                onChange={(e) => setPhotoCommentText(e.target.value)}
                                rows={3}
                                placeholder="Bv. Lichtsterkte verlagen, bureau leegmaken, plant rechts van het kader weghalen…"
                                className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] text-sm"
                                data-testid="photo-comment-text"
                            />

                            <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                                <div className="text-[11px] text-zinc-500 inline-flex items-center gap-2">
                                    {annotationDataUrl ? (
                                        <span className="inline-flex items-center gap-1 text-[#E5A93D]"><ImageIcon className="h-3 w-3" /> Tekening klaar</span>
                                    ) : (
                                        <span>{viewerName ? `Verstuurd als ${viewerName}` : "Je naam wordt gevraagd vóór verzenden"}</span>
                                    )}
                                </div>
                                <Button
                                    onClick={submitPhotoComment}
                                    disabled={photoCommentSubmitting || !photoCommentText.trim()}
                                    className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-10 px-5 active:scale-[0.98]"
                                    data-testid="submit-photo-comment-btn"
                                >
                                    {photoCommentSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Versturen"}
                                </Button>
                            </div>

                            {/* Existing comments on this photo */}
                            {(commentsByPhoto[lightboxIdx] || []).length > 0 && (
                                <div className="mt-5 pt-4 border-t border-zinc-100 space-y-2" data-testid="photo-comments-list">
                                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                                        Eerdere opmerkingen ({(commentsByPhoto[lightboxIdx] || []).length})
                                    </div>
                                    {(commentsByPhoto[lightboxIdx] || []).map((c) => (
                                        <div key={c.id} className="bg-[#F4F4F5] border border-zinc-100 rounded-sm p-3" data-testid={`photo-comment-${c.id}`}>
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <span className="text-sm font-medium text-zinc-950">{c.user_name}</span>
                                                <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">Gast</span>
                                                <span className="text-[11px] text-zinc-500">· {fmtDate(c.created_at)}</span>
                                                {c.annotation_url && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveAnnotationUrl(activeAnnotationUrl === c.annotation_url ? null : c.annotation_url)}
                                                        className={`ml-auto inline-flex items-center gap-1 text-[11px] px-2 h-6 rounded-sm transition ${
                                                            activeAnnotationUrl === c.annotation_url
                                                                ? "bg-[#E5A93D] text-black border border-[#E5A93D]"
                                                                : "bg-zinc-100 hover:bg-zinc-200 text-zinc-950 border border-zinc-200"
                                                        }`}
                                                        data-testid={`toggle-annotation-${c.id}`}
                                                    >
                                                        <ImageIcon className="h-3 w-3" />
                                                        {activeAnnotationUrl === c.annotation_url ? "Tekening verbergen" : "Tekening tonen"}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="text-sm text-zinc-800 whitespace-pre-wrap">{c.text}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Hint */}
                    {!drawMode && (
                        <div className="text-[11px] text-zinc-500 pb-4">
                            ESC om te sluiten · ← → om te bladeren
                        </div>
                    )}
                </div>
            )}

            {needsName && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setNeedsName(false)}>
                    <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="name-modal">
                        <h3 className="font-display font-bold text-xl mb-1">Hoe heet je?</h3>
                        <p className="text-sm text-zinc-500 mb-4">De admin ziet je naam bij elke opmerking.</p>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Naam</Label>
                        <Input
                            value={tempName}
                            onChange={(e) => setTempName(e.target.value)}
                            placeholder="bv. Jan Janssens"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === "Enter") saveName(); }}
                            className="h-11 bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                            data-testid="name-input"
                        />
                        <div className="flex items-center gap-2 mt-4">
                            <Button onClick={saveName} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="name-save-btn">
                                Bevestigen
                            </Button>
                            <button onClick={() => setNeedsName(false)} className="text-sm text-zinc-500 hover:text-zinc-950">Annuleren</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CommentItem({ c, onJump }) {
    const isAdmin = c.user_role === "admin";
    const isGuest = c.user_role === "guest";
    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-4" data-testid="comment-item">
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    onClick={onJump}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 h-7 rounded-sm bg-[#E5A93D]/15 text-[#E5A93D] border border-[#E5A93D]/30 hover:bg-[#E5A93D]/25 transition active:scale-95"
                    data-testid="comment-jump-btn"
                >
                    <Clock className="h-3 w-3" /> {mmss(c.timestamp_seconds)}
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-950">{c.user_name}</span>
                        {isAdmin && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#E5A93D] bg-[#E5A93D]/10 border border-[#E5A93D]/30 rounded-sm px-1.5 py-0.5">
                                <ShieldCheck className="h-3 w-3" /> Admin
                            </span>
                        )}
                        {isGuest && (
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">
                                Gast
                            </span>
                        )}
                        <span className="text-[11px] text-zinc-500">· {fmtDate(c.created_at)}</span>
                    </div>
                    <div className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">{c.text}</div>
                </div>
            </div>
        </div>
    );
}
